import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Button, Form } from "react-bootstrap";
import { Form as IKForm, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { Home, ClipboardCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getActivityTypes, requestActivity } from "../actions/activityActions";
import { IActivityType } from "../models/IAsset";
import { Option } from "../models/types";
import { SingleValue } from "react-select";
import ScannerField from "../components/utilities/ScannerField";
import FileUploadField from "../components/utilities/FileUpload";
import AuthService from "../services/AuthService";
import { useTranslation } from "react-i18next";
import UIButton from "../components/utilities/UIButton";

interface Props {
    dispatch: Function;
    activityTypes: IActivityType[];
}

interface FormValues {
    activity_type_id: number | "";
    asset_id: SingleValue<Option>;
    file: File | null;
    remark: string;
    location_id: SingleValue<Option>;
    status: string;
    cost?: number | "";
}

const LogActivity: React.FC<Props> = ({ dispatch, activityTypes }) => {
    const nav = useNavigate();
    const { i18n } = useTranslation();
    // @ts-ignore
    const t = (txt: string) => i18n.t(txt);
    const user = AuthService.getUser();

    useEffect(() => {
        dispatch(getActivityTypes());
    }, [dispatch]);

    const initialValues: FormValues = {
        activity_type_id: "",
        asset_id: null,
        file: null,
        remark: "",
        location_id: null,
        status: "finished",
        cost: "",
    };

    const validationSchema = Yup.object({
        activity_type_id: Yup.number()
            .transform((val, orig) => (orig === "" ? undefined : Number(orig)))
            .typeError(t("validation.activity_type_required"))
            .required(t("validation.activity_type_required")),
        asset_id: Yup.object()
            .nullable()
            .required(t("validation.asset_required")),
        file: Yup.mixed<File>()
            .required(t("validation.picture_required"))
            .test("fileSize", t("validation.file_too_large"), (value) => !value || value.size <= 1024 * 1024)
            .test("fileFormat", t("validation.unsupported_format"), (value) => !value || ["image/jpeg", "image/png"].includes(value.type)),
        remark: Yup.string().trim().required(t("validation.remark_required")),
        status: Yup.string().required(t("validation.status_required")),
        cost: Yup.number()
            .transform((val, orig) => (orig === "" ? undefined : Number(orig)))
            .nullable()
            .typeError(t("validation.cost_number")),
    });

    const handleSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) => {
        const activityTypeId = values.activity_type_id === "" ? null : Number(values.activity_type_id);
        const selectedType = activityTypes.find((type) => type.id === activityTypeId);
        const isCostIncurring = Boolean(selectedType?.cost_incurring);
        const data: any = {
            asset_id: values.asset_id?.value,
            activity_type_id: activityTypeId ?? "",
            remark: values.remark,
            file: values.file,
            status: values.status,
            cost: isCostIncurring ? (values.cost === "" ? "" : values.cost) : "",
        };

        if(values.location_id?.value) data.location_id = values.location_id?.value;

        dispatch(requestActivity(data, "log-activity-submit", true));

        actions.setSubmitting(false);
    };

    return (
        <div className="container py-3">
            <nav className="breadcrumb-clean" aria-label="breadcrumb">
                <span className="crumb-link" onClick={() => nav("/")}>
                    <Home size={18} />
                </span>
                <span className="crumb-sep">/</span>
                <span className="crumb-current">{t("log_activity")}</span>
            </nav>

            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
                    const activityTypeId = values.activity_type_id === "" ? null : Number(values.activity_type_id);
                    const selectedType = activityTypes.find((type) => type.id === activityTypeId);
                    const showCost = Boolean(selectedType?.cost_incurring);
                    return (
                    <IKForm noValidate>
                        <Form.Label
                            className="required-label"
                            style={{ paddingTop: "10px", paddingBottom: "0px", marginBottom: "0px", textTransform: "capitalize"}}
                        >
                            {t("activity.type")}
                        </Form.Label>
                        <Form.Select
                            name="activity_type_id"
                            value={values.activity_type_id}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={Boolean(touched.activity_type_id && errors.activity_type_id)}
                        >
                            <option value="">{t("select_activity_type")}</option>
                            {activityTypes.map((type) => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.activity_type_id}
                        </Form.Control.Feedback>

                        <ScannerField label="asset" scanOnload={false} target="asset" name="asset_id" required />
                        {touched.asset_id && errors.asset_id && (
                            <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                                {String(errors.asset_id)}
                            </Form.Control.Feedback>
                        )}

                        <FileUploadField label="add_picture" name="file" required />

                        <Form.Label
                            className="required-label"
                            style={{ paddingTop: "10px", paddingBottom: "0px", marginBottom: "0px", textTransform: "capitalize"}}
                        >
                            {t("remark")}
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            name="remark"
                            value={values.remark}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={Boolean(touched.remark && errors.remark)}
                        />
                        <Form.Control.Feedback type="invalid">{errors.remark}</Form.Control.Feedback>

                        <ScannerField label="location" scanOnload={false} name="location_id" />

                        <Form.Label
                            className="required-label"
                            style={{ paddingTop: "10px", paddingBottom: "0px", marginBottom: "0px", textTransform: "capitalize"}}
                        >
                            {t("status")}
                        </Form.Label>
                        <Form.Select
                            name="status"
                            value={values.status}
                            onChange={(e) => setFieldValue("status", e.target.value)}
                            onBlur={handleBlur}
                            disabled={true}
                            isInvalid={Boolean(touched.status && errors.status)}
                        >
                            <option value="finished">{t("finished")}</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.status}</Form.Control.Feedback>

                        {showCost ? (
                            <>
                                <Form.Label  style={{ paddingTop: "10px", paddingBottom: "0px", marginBottom: "0px", textTransform: "capitalize"}}>{t("cost")}</Form.Label>
                                <Form.Control
                                    as="input"
                                    type="number"
                                    name="cost"
                                    value={values.cost ?? ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={Boolean(touched.cost && errors.cost)}
                                />
                                <Form.Control.Feedback type="invalid">{errors.cost}</Form.Control.Feedback>
                            </>
                        ) : null}

                        <UIButton variant="primary" type="submit" label={t("submit")} id="log-activity-submit" className="mt-3 w-100" />

                    </IKForm>
                )}}
            </Formik>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    activityTypes: state.asset.activityTypes,
});

export default connect(mapStateToProps)(LogActivity);
